import { create } from 'zustand';
import { permissionActions } from './permission.action';

export type PermissionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface PermissionState {
  permissions: string[];
  status: PermissionsStatus;
  /** User the loaded permissions belong to. */
  userId: string | null;
  fetchPermissions: (userId: string) => Promise<void>;
  clearPermissions: () => void;
}

/**
 * Not persisted on purpose: permissions are re-fetched each session so a
 * revoked permission never survives a reload. Until loaded, callers should
 * treat every permission as missing.
 */
export const usePermissionStore = create<PermissionState>()((set, get) => ({
  permissions: [],
  status: 'idle',
  userId: null,

  fetchPermissions: async (userId: string) => {
    const { status, userId: loadedUserId } = get();
    if (
      loadedUserId === userId &&
      (status === 'loading' || status === 'loaded')
    ) {
      return;
    }

    set({ permissions: [], status: 'loading', userId });
    try {
      const permissions =
        await permissionActions.getPermissionsForCurrentUser();
      if (get().userId === userId) {
        set({ permissions, status: 'loaded' });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      if (get().userId === userId) {
        set({ permissions: [], status: 'error' });
      }
    }
  },

  clearPermissions: () =>
    set({ permissions: [], status: 'idle', userId: null }),
}));
